import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Search from '@carbon/react/es/components/Search';
import Folder from '@carbon/icons-react/es/Folder';
import Location from '@carbon/icons-react/es/Location';
import VideoIcon from '@carbon/icons-react/es/Video';
import MachineLearningModel from '@carbon/icons-react/es/MachineLearningModel';
import Cube from '@carbon/icons-react/es/Cube';
import type { SearchEntityType, SearchHit } from '@ddp/shared';

import { useT, type MessageKey } from '../../i18n/index.js';
import { useGlobalSearch } from './use-global-search.js';

/** Icône et libellé de groupe par nature d'entité. L'ordre du tableau fixe l'ordre d'affichage. */
const TYPE_META: { type: SearchEntityType; icon: ComponentType; groupKey: MessageKey }[] = [
  { type: 'video', icon: VideoIcon, groupKey: 'search.group.videos' },
  { type: 'campaign', icon: Folder, groupKey: 'search.group.projects' },
  { type: 'fieldSession', icon: Location, groupKey: 'search.group.missions' },
  { type: 'model', icon: MachineLearningModel, groupKey: 'search.group.models' },
  { type: 'libraryModel', icon: Cube, groupKey: 'search.group.libraryModels' },
];

/**
 * Barre de recherche transverse, en tête de page à droite du logo (ADR-0015).
 *
 * La frappe interroge `/search` (anti-rebond dans le hook) ; les résultats s'affichent dans un
 * panneau déroulant groupé par nature. Un clic navigue vers la meilleure destination disponible :
 * une vidéo a sa page propre, les autres pointent vers leur section (pas encore de page détail).
 */
export function GlobalSearch() {
  const t = useT();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { hits, loading, error, active } = useGlobalSearch(term);

  // Fermer au clic hors du composant : un panneau flottant qui reste ouvert est un piège.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const goToHit = (hit: SearchHit) => {
    setOpen(false);
    setTerm('');
    switch (hit.type) {
      case 'video':
        void navigate({ to: '/videos/$videoId', params: { videoId: hit.id } });
        break;
      case 'model':
        void navigate({ to: '/model' });
        break;
      // Un modèle de bibliothèque vit dans la section « Bibliothèque » de la page Model (ADR-0017),
      // atteinte par son fragment d'URL — pas de page détail par item.
      case 'libraryModel':
        void navigate({ to: '/model', hash: 'bibliotheque' });
        break;
      // Campagnes et sessions n'ont pas (encore) de page détail : on renvoie vers Data collection.
      case 'campaign':
      case 'fieldSession':
        void navigate({ to: '/data' });
        break;
    }
  };

  // Le panneau ne s'ouvre qu'une fois le seuil atteint (le hook expose `active`).
  const showPanel = open && active;

  return (
    <div className="ddp-search" ref={containerRef}>
      <Search
        size="lg"
        labelText={t('search.label')}
        placeholder={t('search.placeholder')}
        value={term}
        closeButtonLabelText={t('search.clear')}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onClear={() => {
          setTerm('');
          setOpen(false);
        }}
      />

      {showPanel && (
        <div className="ddp-search__panel" role="listbox" aria-label={t('search.label')}>
          {loading && hits.length === 0 && (
            <p className="ddp-search__status">{t('search.searching')}</p>
          )}
          {error && <p className="ddp-search__status ddp-search__status--error">{error}</p>}
          {!loading && !error && hits.length === 0 && (
            <p className="ddp-search__status">{t('search.empty')}</p>
          )}

          {TYPE_META.map(({ type, icon: Icon, groupKey }) => {
            const group = hits.filter((h) => h.type === type);
            if (group.length === 0) return null;
            return (
              <div key={type} className="ddp-search__group">
                <p className="ddp-search__group-label">{t(groupKey)}</p>
                {group.map((hit) => (
                  <button
                    key={`${hit.type}-${hit.id}`}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="ddp-search__hit"
                    onClick={() => goToHit(hit)}
                  >
                    <span className="ddp-search__hit-icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <span className="ddp-search__hit-text">
                      <span className="ddp-search__hit-title">{hit.title}</span>
                      {hit.subtitle && (
                        <span className="ddp-search__hit-subtitle">{hit.subtitle}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
